import { Badge } from "@nfp-tracker/components";
import { Eye } from "lucide-react";
import { useInstructorClient } from "./InstructorClientContext";

const formatDate = (value: string | null) => {
  if (!value) return null;
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Sits directly under the page header on every client-chart route and answers "whose data is
 * this, and can I change it?" at a glance. It is a sibling of the header, outside the page's
 * scroll container, so it stays put while the chart scrolls. The switch / close controls live in
 * the page header (AppShellLayout's header actions).
 */
export const ClientViewBanner = () => {
  const { activeClient } = useInstructorClient();

  if (!activeClient) return null;

  const summary = activeClient.summary;
  const lastEntry = summary ? formatDate(summary.lastObservationDate) : null;
  const meta = summary
    ? [
        `${summary.observationCount} observation${summary.observationCount === 1 ? "" : "s"}`,
        lastEntry ? `last entry ${lastEntry}` : "no entries yet",
      ].join(" · ")
    : null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="border-b border-primary/20 bg-primary/5 text-sm"
    >
      <div className="mx-auto flex w-full max-w-3xl flex-wrap items-center gap-x-3 gap-y-2 px-4 py-1.5 md:px-5">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 gap-y-1">
          <Eye className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="min-w-0 truncate">
            Viewing <span className="font-semibold">{activeClient.displayName}</span>'s chart
          </span>
          <Badge variant="outline" className="shrink-0 border-primary/40 text-primary">
            Read only
          </Badge>
          {meta ? (
            <span className="hidden text-xs text-muted-foreground sm:inline">{meta}</span>
          ) : null}
        </div>
      </div>
    </div>
  );
};
